/** Top toolbar: file operations. Touch targets are ≥44px for tablet use. */
import { useEffect, useRef, useState } from "react";

export interface ToolbarProps {
  filename: string;
  dirty: boolean;
  canShare: boolean;
  onNew: () => void;
  onOpen: (file: File) => void;
  onSave: () => void;
  onShare: () => void;
  onTitlePage: () => void;
  onExportFountain: () => void;
  onPrint: () => void;
  onRename: () => void;
}

export function Toolbar({
  filename,
  dirty,
  canShare,
  onNew,
  onOpen,
  onSave,
  onShare,
  onTitlePage,
  onExportFountain,
  onPrint,
  onRename,
}: ToolbarProps) {
  const fileInput = useRef<HTMLInputElement | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const close = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [menuOpen]);

  const runMenu = (fn: () => void) => () => {
    setMenuOpen(false);
    fn();
  };

  return (
    <header className="sp-topbar">
      <span className="sp-title">ScriptPad</span>

      <div className="sp-toolbar-actions">
        <button className="sp-btn" onClick={onNew} title="New script">
          New
        </button>
        <button
          className="sp-btn"
          onClick={() => fileInput.current?.click()}
          title="Open .fadein"
        >
          Open
        </button>
        <button className="sp-btn sp-btn-primary" onClick={onSave} title="Save .fadein">
          Save
        </button>
        {canShare && (
          <button className="sp-btn" onClick={onShare} title="Share / Send">
            Share
          </button>
        )}
        <div className="sp-menu" ref={menuRef}>
          <button
            className="sp-btn"
            onClick={() => setMenuOpen((o) => !o)}
            aria-haspopup="true"
            aria-expanded={menuOpen}
            title="More"
          >
            ⋯
          </button>
          {menuOpen && (
            <div className="sp-menu-list" role="menu">
              <button className="sp-menu-item" onClick={runMenu(onTitlePage)}>
                Title Page…
              </button>
              <button className="sp-menu-item" onClick={runMenu(onPrint)}>
                Print / PDF
              </button>
              <button className="sp-menu-item" onClick={runMenu(onExportFountain)}>
                Export Fountain
              </button>
            </div>
          )}
        </div>
      </div>

      <button
        className="sp-filename"
        onClick={onRename}
        title="Rename file"
        aria-live="polite"
      >
        {filename}
        {dirty ? " •" : ""}
        <span className="sp-rename-hint">✎</span>
      </button>

      <input
        ref={fileInput}
        type="file"
        accept=".fadein"
        style={{ display: "none" }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onOpen(file);
          e.target.value = ""; // allow re-opening the same file
        }}
      />
    </header>
  );
}
